import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { FileArchive, Building, Plus } from 'lucide-react';
import { DrawingDetailModal, PermitDetailModal } from '../components/DetailModals';

const AUTHORITIES = ["Dubai Municipality","Dubai Civil Defence","DEWA","RTA","Developer NOC","Trakheesi","RERA","DLD","Etisalat/du"];

export default function Drawings() {
  const [drawings, setDrawings] = useState([]);
  const [permits, setPermits] = useState([]);
  const [props, setProps] = useState([]);
  const [addD, setAddD] = useState(false);
  const [addP, setAddP] = useState(false);
  const [viewD, setViewD] = useState(null);
  const [viewP, setViewP] = useState(null);

  const load = () => Promise.all([api.get('/drawings'), api.get('/permits'), api.get('/properties')])
      .then(([d, p, pr]) => { setDrawings(d.data); setPermits(p.data); setProps(pr.data); });
  useEffect(() => { load(); }, []);

  const propTitle = (id) => props.find(p => p.id === id)?.title || '—';

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="drawings-page">
      <div>
        <div className="overline">Compliance</div>
        <h1 className="font-serif text-4xl">Drawings, permits & authorities</h1>
        <p className="text-slate-600 mt-2">Every stamp, every submission, every NOC — in one place.</p>
      </div>

      <div className="glass p-6">
        <div className="flex items-center gap-2 mb-4 justify-between">
          <div className="flex items-center gap-2"><FileArchive size={18}/> <div className="font-serif text-2xl">Drawing register</div></div>
          <button onClick={()=>setAddD(true)} data-testid="add-drawing-btn" className="btn-primary text-xs flex items-center gap-1"><Plus size={12}/> New drawing</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="overline text-left border-b border-slate-200/60"><th className="py-2">Property</th><th>Discipline</th><th>Title</th><th>Rev</th><th>Consultant</th><th>Status</th></tr></thead>
          <tbody>
            {drawings.map(d => (
              <tr key={d.id} onClick={() => setViewD(d.id)} data-testid={`drawing-row-${d.id}`}
                  className="border-b border-slate-100/70 cursor-pointer hover:bg-white/50">
                <td className="py-3 text-xs">{propTitle(d.property_id)}</td>
                <td>{d.discipline}</td>
                <td className="max-w-xs truncate">{d.title}</td>
                <td className="font-mono text-xs">{d.revision}</td>
                <td className="text-xs">{d.consultant}</td>
                <td><span className={`status-pill ${d.status === 'approved' ? 'status-success' : d.status === 'superseded' ? 'status-neutral' : 'status-warning'}`}>{d.status?.replace('_',' ')}</span></td>
              </tr>
            ))}
            {drawings.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No drawings yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="glass p-6">
        <div className="flex items-center gap-2 mb-4 justify-between">
          <div className="flex items-center gap-2"><Building size={18}/> <div className="font-serif text-2xl">Authority submissions</div></div>
          <button onClick={()=>setAddP(true)} data-testid="add-permit-btn" className="btn-primary text-xs flex items-center gap-1"><Plus size={12}/> Log submission</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="overline text-left border-b border-slate-200/60"><th className="py-2">Property</th><th>Authority</th><th>Permit</th><th>Ref #</th><th>Fee (AED)</th><th>Status</th></tr></thead>
          <tbody>
            {permits.map(pm => (
              <tr key={pm.id} onClick={() => setViewP(pm.id)} data-testid={`permit-row-${pm.id}`}
                  className="border-b border-slate-100/70 cursor-pointer hover:bg-white/50">
                <td className="py-3 text-xs">{propTitle(pm.property_id)}</td>
                <td>{pm.authority}</td>
                <td className="max-w-xs truncate">{pm.permit_type}</td>
                <td className="font-mono text-xs">{pm.reference_number}</td>
                <td>{pm.fee_paid_aed?.toLocaleString()}</td>
                <td><span className={`status-pill ${pm.status === 'approved' ? 'status-success' : pm.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>{pm.status?.replace('_',' ')}</span></td>
              </tr>
            ))}
            {permits.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No permits yet.</td></tr>}
          </tbody>
        </table>

        <div className="mt-4 flex flex-wrap gap-2">
          <div className="overline w-full mb-1">Tracked authorities</div>
          {AUTHORITIES.map(a => <span key={a} className="status-pill status-neutral text-xs">{a}</span>)}
        </div>
      </div>

      {addD && <DrawingModal props={props} onClose={()=>setAddD(false)} onSaved={()=>{setAddD(false); load();}}/>}
      {addP && <PermitModal props={props} onClose={()=>setAddP(false)} onSaved={()=>{setAddP(false); load();}}/>}
      {viewD && <DrawingDetailModal drawingId={viewD} onClose={() => setViewD(null)} onUpdate={load}/>}
      {viewP && <PermitDetailModal permitId={viewP} onClose={() => setViewP(null)} onUpdate={load}/>}
    </div>
  );
}

function DrawingModal({ props, onClose, onSaved }) {
  const [f, setF] = useState({ property_id: '', title: '', discipline: 'Architectural', revision: 'Rev A', consultant: '', consultant_license: '', status: 'for_approval' });
  const submit = async (e) => { e.preventDefault(); await api.post('/drawings', f); onSaved(); };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-lg p-6" data-testid="drawing-modal">
        <div className="font-serif text-2xl mb-4">New drawing</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-sm">Property
            <select required value={f.property_id} onChange={(e)=>setF({...f, property_id:e.target.value})} data-testid="drawing-property"
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              <option value="">Choose…</option>
              {props.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <label className="col-span-2 text-sm">Title
            <input required value={f.title} onChange={(e)=>setF({...f, title:e.target.value})} data-testid="drawing-title"
                   className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="text-sm">Discipline
            <select value={f.discipline} onChange={(e)=>setF({...f, discipline:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              {['Architectural','MEP','Structural','Interior'].map(d=><option key={d}>{d}</option>)}
            </select>
          </label>
          <label className="text-sm">Revision
            <input value={f.revision} onChange={(e)=>setF({...f, revision:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="text-sm">Consultant
            <input value={f.consultant} onChange={(e)=>setF({...f, consultant:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="text-sm">Licence #
            <input value={f.consultant_license} onChange={(e)=>setF({...f, consultant_license:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="col-span-2 text-sm">Status
            <select value={f.status} onChange={(e)=>setF({...f, status:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              {['for_approval','approved','superseded','as_built'].map(s=><option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" className="btn-primary text-sm" data-testid="drawing-save">Save</button>
        </div>
      </form>
    </div>
  );
}

function PermitModal({ props, onClose, onSaved }) {
  const [f, setF] = useState({ property_id: '', authority: 'Dubai Municipality', permit_type: '', reference_number: '', submission_date: new Date().toISOString().slice(0,10), fee_paid_aed: 0, status: 'submitted' });
  const submit = async (e) => { e.preventDefault(); await api.post('/permits', f); onSaved(); };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-lg p-6" data-testid="permit-modal">
        <div className="font-serif text-2xl mb-4">Log authority submission</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-sm">Property
            <select required value={f.property_id} onChange={(e)=>setF({...f, property_id:e.target.value})} data-testid="permit-property"
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              <option value="">Choose…</option>
              {props.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <label className="text-sm">Authority
            <select value={f.authority} onChange={(e)=>setF({...f, authority:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              {AUTHORITIES.map(a=><option key={a}>{a}</option>)}
            </select>
          </label>
          <label className="text-sm">Type
            <input required value={f.permit_type} onChange={(e)=>setF({...f, permit_type:e.target.value})} data-testid="permit-type"
                   className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="text-sm">Reference #
            <input value={f.reference_number} onChange={(e)=>setF({...f, reference_number:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="text-sm">Fee (AED)
            <input type="number" value={f.fee_paid_aed} onChange={(e)=>setF({...f, fee_paid_aed:+e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
          </label>
          <label className="col-span-2 text-sm">Status
            <select value={f.status} onChange={(e)=>setF({...f, status:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
              {['submitted','under_review','approved','rejected','resubmit'].map(s=><option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" className="btn-primary text-sm" data-testid="permit-save">Save</button>
        </div>
      </form>
    </div>
  );
}
